(() => {
  const initConstruction = (root) => {
    const construction = root.querySelector('[data-xlab-construction]');
    if (!construction || construction.dataset.constructionReady === 'true') return;
    construction.dataset.constructionReady = 'true';
    const tabs = [...construction.querySelectorAll('[data-construction-tab]')];
    const panels = [...construction.querySelectorAll('[data-construction-panel]')];
    const layers = [...construction.querySelectorAll('[data-construction-layer]')];
    const activate = (key, focus = false) => {
      if (!key) return;
      construction.dataset.active = key;
      tabs.forEach((tab) => {
        const active = tab.dataset.constructionTab === key;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus({ preventScroll: true });
      });
      panels.forEach((panel) => {
        const active = panel.dataset.constructionPanel === key;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      layers.forEach((layer) => layer.classList.toggle('is-active', layer.dataset.constructionLayer === key));
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab.dataset.constructionTab));
      tab.addEventListener('keydown', (event) => {
        let next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        activate(tabs[next].dataset.constructionTab, true);
      });
    });
    root.querySelectorAll('[data-construction-target]').forEach((link) => {
      link.addEventListener('click', () => activate(link.dataset.constructionTarget));
    });
    activate(construction.dataset.active || tabs[0]?.dataset.constructionTab);
  };

  const init = (root) => {
    if (!root || root.dataset.xlabReady === 'true') return;
    root.dataset.xlabReady = 'true';
    initConstruction(root);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(() => root.classList.add('is-ready'));

    const revealEls = [...root.querySelectorAll('[data-xlab-reveal]')];
    if (reduced || !('IntersectionObserver' in window)) revealEls.forEach((el) => el.classList.add('is-visible'));
    else {
      const observer = new IntersectionObserver((entries, io) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      }, { threshold: .08, rootMargin: '0px 0px -6% 0px' });
      revealEls.forEach((el) => observer.observe(el));
    }

    const anatomy = root.querySelector('[data-xlab-anatomy]');
    if (anatomy) {
      if (reduced || !('IntersectionObserver' in window)) anatomy.classList.add('is-visible');
      else {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            anatomy.classList.add('is-visible');
            observer.disconnect();
          }
        }, { threshold: .14 });
        observer.observe(anatomy);
      }
    }

    const process = root.querySelector('[data-xlab-process]');
    const steps = process ? [...process.querySelectorAll('[data-process-step]')] : [];
    if (process && steps.length) {
      if (reduced) {
        process.style.setProperty('--process-progress', '1');
        steps.forEach((step) => step.classList.add('is-passed'));
      } else {
        let ticking = false;
        const update = () => {
          const rect = process.getBoundingClientRect();
          const vh = window.innerHeight || document.documentElement.clientHeight;
          const start = vh * .78;
          const end = vh * .2;
          const total = Math.max(rect.height + start - end, 1);
          const progress = Math.max(0, Math.min(1, (start - rect.top) / total));
          process.style.setProperty('--process-progress', progress.toFixed(3));
          const activeIndex = progress < .025 ? -1 : Math.min(steps.length - 1, Math.floor(progress * steps.length));
          steps.forEach((step, index) => {
            step.classList.toggle('is-active', index === activeIndex);
            step.classList.toggle('is-passed', activeIndex >= 0 && index < activeIndex);
          });
          ticking = false;
        };
        const requestUpdate = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        };
        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate, { passive: true });
        update();
      }
    }

    const tests = [...root.querySelectorAll('[data-xlab-test]')];
    if (tests.length && window.innerWidth >= 750 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          tests.forEach((item) => item.classList.remove('is-active'));
          entry.target.classList.add('is-active');
        });
      }, { threshold: .62, rootMargin: '-18% 0px -18% 0px' });
      tests.forEach((test) => observer.observe(test));
    }

    const marquee = root.querySelector('.xlab-now__marquee');
    if (marquee && !marquee.dataset.xlabMarqueeReady) {
      const original = marquee.firstElementChild;
      if (original) {
        const clone = original.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        marquee.appendChild(clone);
        marquee.dataset.xlabMarqueeReady = 'true';
      }
    }

    const faqItems = [...root.querySelectorAll('.xlab-faq details')];
    faqItems.forEach((item) => item.addEventListener('toggle', () => {
      if (!item.open) return;
      faqItems.forEach((other) => { if (other !== item) other.removeAttribute('open'); });
    }));
  };

  const boot = () => document.querySelectorAll('[data-xlab]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.matches?.('[data-xlab]') ? event.target : event.target.querySelector?.('[data-xlab]');
    if (!root) return;
    delete root.dataset.xlabReady;
    init(root);
  });
})();

